export type SupabaseHeaderProfile = {
  email: string;
  displayName: string;
  avatarUrl: string;
};

/** Read only the public profile fields from the SSR auth cookie, without loading Supabase JS on public pages. */
export function readSupabaseHeaderProfile(cookieHeader: string, supabaseUrl?: string): SupabaseHeaderProfile | null {
  if (!supabaseUrl) return null;
  let storageKey: string;
  try {
    const projectRef = new URL(supabaseUrl).hostname.split(".")[0];
    if (!projectRef) return null;
    storageKey = `sb-${projectRef}-auth-token`;
  } catch {
    return null;
  }

  const cookies = cookieHeader.split(";").flatMap((part) => {
    const separator = part.indexOf("=");
    if (separator < 1) return [];
    const name = part.slice(0, separator).trim();
    const encodedValue = part.slice(separator + 1).trim();
    try {
      return [{ name, value: decodeURIComponent(encodedValue) }];
    } catch {
      return [{ name, value: encodedValue }];
    }
  });

  const chunkPrefix = `${storageKey}.`;
  const chunks = cookies
    .flatMap((cookie) => {
      if (!cookie.name.startsWith(chunkPrefix)) return [];
      const index = cookie.name.slice(chunkPrefix.length);
      return /^\d+$/.test(index) ? [{ index: Number(index), value: cookie.value }] : [];
    })
    .sort((left, right) => left.index - right.index);
  const storedValue = chunks.length
    ? chunks.map((chunk) => chunk.value).join("")
    : cookies.find((cookie) => cookie.name === storageKey)?.value;
  if (!storedValue) return null;

  try {
    const json = storedValue.startsWith("base64-")
      ? decodeBase64Url(storedValue.slice("base64-".length))
      : storedValue;
    const session: unknown = JSON.parse(json);
    if (!session || typeof session !== "object") return null;
    const user = "user" in session && session.user && typeof session.user === "object"
      ? session.user as Record<string, unknown>
      : null;
    if (!user || typeof user.email !== "string") return null;
    const metadata = user.user_metadata && typeof user.user_metadata === "object"
      ? user.user_metadata as Record<string, unknown>
      : {};
    return {
      email: user.email,
      displayName: typeof metadata.display_name === "string" ? metadata.display_name : "",
      avatarUrl: typeof metadata.avatar_url === "string" ? metadata.avatar_url : "",
    };
  } catch {
    return null;
  }
}

function decodeBase64Url(value: string) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
